import { format, formatDistanceToNow } from 'date-fns'
import { ja } from 'date-fns/locale'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'
import type { Assignment } from '../lib/useAssignments'

interface Props {
  assignments: Assignment[]
}

/**
 * Vertical timeline of manager_assignments for a liver.
 * Topmost item is the current active assignment (ended_at IS NULL).
 * Each prior item shows its date range and (if present) handoff notes.
 */
export function AssignmentTimeline({ assignments }: Props) {
  if (assignments.length === 0) {
    return (
      <div className="text-xs text-gray-400 italic py-2">
        担当履歴がまだありません
      </div>
    )
  }

  return (
    <ol className="relative space-y-3 pl-5">
      {/* Vertical guide line */}
      <div
        className="absolute left-[7px] top-2 bottom-2 w-px bg-gray-200"
        aria-hidden
      />
      {assignments.map((a, idx) => (
        <AssignmentRow key={a.id} assignment={a} isFirst={idx === 0} />
      ))}
    </ol>
  )
}

function AssignmentRow({
  assignment,
  isFirst
}: {
  assignment: Assignment
  isFirst: boolean
}) {
  const [expanded, setExpanded] = useState(isFirst)
  const isActive = assignment.ended_at === null

  const periodText = (() => {
    const started = new Date(assignment.started_at)
    const startedFmt = format(started, 'yyyy/MM/dd', { locale: ja })
    if (isActive) {
      const since = formatDistanceToNow(started, { locale: ja, addSuffix: false })
      return `${startedFmt} 〜 現在  (${since})`
    }
    const ended = new Date(assignment.ended_at!)
    const endedFmt = format(ended, 'yyyy/MM/dd', { locale: ja })
    return `${startedFmt} 〜 ${endedFmt}`
  })()

  const hasDetails = !!assignment.change_reason || !!assignment.handoff_notes

  return (
    <li className="relative">
      <span
        className={`absolute -left-5 top-1.5 w-3 h-3 rounded-full border-2 ${
          isActive ? 'bg-sky-500 border-sky-200' : 'bg-white border-gray-300'
        }`}
        aria-hidden
      />
      <div className="text-sm">
        <div className="flex items-baseline gap-2">
          <span className="font-medium text-gray-800">
            {assignment.manager?.name ?? assignment.manager?.email ?? '(不明)'}
          </span>
          {isActive && (
            <span className="text-[10px] uppercase tracking-wide text-sky-600 font-semibold">
              現担当
            </span>
          )}
        </div>
        <div className="text-[11px] text-gray-500 tabular-nums">{periodText}</div>
        {hasDetails && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-1 text-[11px] text-sky-600 hover:text-sky-700 inline-flex items-center gap-0.5"
          >
            {expanded ? (
              <>
                <ChevronUp className="w-3 h-3" />
                詳細を閉じる
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3" />
                詳細を見る
              </>
            )}
          </button>
        )}
        {expanded && hasDetails && (
          <div className="mt-2 space-y-1 text-[12px] bg-gray-50 rounded-lg p-2 border border-gray-100">
            {assignment.change_reason && (
              <div>
                <span className="text-gray-400">変更理由:</span>{' '}
                <span className="text-gray-700">{assignment.change_reason}</span>
              </div>
            )}
            {assignment.predecessor && (
              <div>
                <span className="text-gray-400">前任:</span>{' '}
                <span className="text-gray-700">
                  {assignment.predecessor.name ?? assignment.predecessor.email}
                </span>
              </div>
            )}
            {assignment.handoff_notes && (
              <div>
                <div className="text-gray-400 mb-0.5">引継ぎノート:</div>
                <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                  {assignment.handoff_notes}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  )
}
